import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { validateHabit, type Schedule } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import {
  createHabit,
  draftFromHabit,
  emptyDraft,
  historyImpact,
  nextVersions,
  PlanLimitError,
  updateHabit,
  type HabitDraft,
} from '../domain/actions';
import { averageAllowed } from '@habitos/assistant/domain/records';
import { HABIT_COLORS, HABIT_ICONS, UNITS } from '../domain/model';
import { setMeta } from '../db/db';
import { Field, Segmented, Switch } from '../ui/Field';
import { HabitIcon } from '../ui/HabitIcon';
import { useToast } from '../ui/Toast';
import { unitLabel } from '../ui/format';

type FreqType = Schedule['type'];

export function HabitEditor() {
  const { id } = useParams();
  const { t, m, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const existing = id ? data.habits.find((h) => h.id === id) : undefined;
  // Borrador inicial opcional (por ejemplo, desde el asistente: "Editar en el formulario").
  const preset = (useLocation().state as { draft?: Partial<HabitDraft> } | null)?.draft;
  const [draft, setDraft] = useState<HabitDraft>(() =>
    existing ? draftFromHabit(existing) : { ...emptyDraft(ctx.today, preset?.kind), ...preset, startDate: ctx.today },
  );
  const [applyHistory, setApplyHistory] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(existing);
  const groups = data.groups.filter((g) => !g.deletedAt).sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

  const set = <K extends keyof HabitDraft>(k: K, v: HabitDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const changeKind = (kind: HabitDraft['kind']) => {
    const base = emptyDraft(ctx.today, kind);
    setDraft((d) => ({
      ...d,
      kind,
      goalType: base.goalType,
      target: base.target,
      icon: d.icon === emptyDraft(ctx.today, d.kind).icon ? base.icon : d.icon,
      color: d.color === emptyDraft(ctx.today, d.kind).color ? base.color : d.color,
      schedule: kind === 'quit' && (d.schedule.type === 'weekly_quota' || d.schedule.type === 'monthly_quota') && d.schedule.mode === 'times' ? { type: 'daily' } : d.schedule,
    }));
  };

  const errors = useMemo(() => {
    if (draft.kind === 'task') return validateHabit({ id: 'x', kind: 'task', startDate: draft.startDate, versions: [], dueDate: draft.dueDate });
    return validateHabit({
      id: 'x',
      kind: draft.kind,
      startDate: draft.startDate,
      endDate: draft.endDate,
      versions: [
        {
          effectiveFrom: draft.startDate,
          goalType: draft.kind === 'quit' ? 'numeric' : draft.goalType,
          target: draft.goalType === 'binary' && draft.kind === 'build' ? 1 : draft.target,
          schedule: draft.schedule,
        },
      ],
    });
  }, [draft]);
  const nameError = submitted && !draft.name.trim() ? t('editor.nameRequired') : null;
  // Unidad propia (FR-HAB-15): se guarda como "singular|plural", la misma forma que las predefinidas.
  // Estado propio y no derivado: escribir "l" de "litro" no debe saltar a la unidad predefinida "l".
  const [customUnit, setCustomMode] = useState(() => draft.unit != null && !(UNITS as readonly string[]).includes(draft.unit));
  const [unitOne = '', unitOther = ''] = customUnit ? (draft.unit ?? '').split('|') : [];
  const setCustomUnit = (one: string, other: string) => {
    const clean = (x: string) => x.replace(/\|/g, '').trimStart();
    const o = clean(one);
    const p = clean(other);
    set('unit', p.trim() && p.trim() !== o.trim() ? `${o}|${p}` : o);
  };

  const errText = (code: string) => (m.editor.errors as Record<string, string>)[code] ?? m.editor.errors.generic;

  const impact = useMemo(() => {
    if (!existing || !applyHistory || existing.kind === 'task') return null;
    return historyImpact(existing, draft, ctx, data.entries, data.overrides, data.vacations);
  }, [existing, applyHistory, draft, ctx, data.entries, data.overrides, data.vacations]);

  const configChanged =
    existing && existing.kind !== 'task' && JSON.stringify(nextVersions(existing, draft, ctx.today, false)) !== JSON.stringify(existing.versions);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!draft.name.trim() || errors.length > 0 || unitMissing) return;
    setSaving(true);
    // Los espacios finales se conservan mientras se escribe ("vaso de agua") y se quitan al guardar.
    const final = customUnit && draft.unit ? { ...draft, unit: draft.unit.split('|').map((x) => x.trim()).join('|') } : draft;
    try {
      if (existing) {
        await updateHabit(existing, final, ctx, applyHistory);
        navigate(`/habits/${existing.id}`, { replace: true });
      } else {
        const newId = await createHabit(final, data.habits);
        await setMeta('hasCreatedHabit', true);
        navigate('/', { replace: true });
        void newId;
      }
    } catch (err) {
      if (err instanceof PlanLimitError) toast.show(t('editor.planLimit', { limit: err.limit }));
      else toast.show(t('errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const freqOptions: FreqType[] =
    draft.kind === 'quit'
      ? ['daily', 'weekdays', 'weekly_quota', 'monthly_quota']
      : ['daily', 'weekdays', 'weekly_quota', 'monthdays', 'monthly_quota', 'interval_days', 'yearly'];

  const setFreq = (type: FreqType) => {
    const quotaMode = draft.kind === 'quit' ? 'amount' : 'times';
    const s: Schedule =
      type === 'daily'
        ? { type }
        : type === 'weekdays'
          ? { type, days: [1, 2, 3, 4, 5] }
          : type === 'weekly_quota'
            ? { type, mode: quotaMode, times: 3 }
            : type === 'monthdays'
              ? { type, days: [1] }
              : type === 'monthly_quota'
                ? { type, mode: quotaMode, times: 8 }
                : type === 'yearly'
                  ? { type }
                  : { type, every: 2 };
    set('schedule', s);
  };

  const s = draft.schedule;
  const isQuota = s.type === 'weekly_quota' || s.type === 'monthly_quota';
  const showTarget = draft.kind === 'quit' || draft.goalType === 'numeric' || (isQuota && s.mode === 'amount');
  const unitMissing = customUnit && showTarget && !unitOne.trim();
  const unitError = submitted && unitMissing ? t('editor.unitRequired') : null;

  return (
    <form onSubmit={onSubmit} noValidate className="stack-l">
      <header className="page-header" style={{ marginBottom: 0 }}>
        <div className="row">
          <button type="button" className="icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <ArrowLeft size={22} aria-hidden="true" />
          </button>
          <h1>{isEdit ? t('editor.editTitle') : t('editor.newTitle')}</h1>
        </div>
      </header>

      {!isEdit && (
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field__label" style={{ marginBottom: 6 }}>
            {t('editor.type')}
          </legend>
          <div className="type-cards">
            {(['build', 'quit', 'task'] as const).map((k) => (
              <label key={k} className="type-card">
                <input type="radio" name="kind" value={k} checked={draft.kind === k} onChange={() => changeKind(k)} />
                <span className="type-card__title">{t(`habit.kinds.${k}`)}</span>
                <span className="type-card__help">{t(`habit.kindHelp.${k}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <section className="form-section">
        <Field label={t('editor.name')} error={nameError}>
          {(p) => (
            <input
              {...p}
              className="input"
              maxLength={80}
              autoFocus={!isEdit}
              placeholder={t('editor.namePlaceholder')}
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t('editor.description')} (${t('common.optional')})`}>
          {(p) => <textarea {...p} className="textarea" maxLength={500} value={draft.description ?? ''} onChange={(e) => set('description', e.target.value)} />}
        </Field>
      </section>

      {draft.kind !== 'task' && (
        <section className="form-section" aria-label={t('editor.goal')}>
          <h2>{t('editor.goal')}</h2>
          {draft.kind === 'build' && (
            <Segmented
              legend={t('editor.goal')}
              value={draft.goalType}
              onChange={(v) => {
                setCustomMode(false);
                setDraft((d) => ({ ...d, goalType: v, target: v === 'binary' ? 1 : d.target > 1 ? d.target : 10, unit: v === 'numeric' ? (d.unit ?? 'min') : null }));
              }}
              options={[
                { value: 'binary', label: t('editor.goalBinary') },
                { value: 'numeric', label: t('editor.goalNumeric') },
              ]}
            />
          )}
          {showTarget && (
            <div className="row row--wrap" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 140px' }}>
                <Field
                  label={draft.kind === 'quit' ? t('editor.limit') : t('editor.target')}
                  help={draft.kind === 'quit' ? t('editor.limitHelp') : undefined}
                  error={submitted && errors.includes('invalid_target') ? errText('invalid_target') : null}
                >
                  {(p) => (
                    <input
                      {...p}
                      className="input num"
                      type="number"
                      inputMode="decimal"
                      min={draft.kind === 'quit' ? 0 : 0.001}
                      step="any"
                      value={Number.isFinite(draft.target) ? draft.target : ''}
                      onChange={(e) => set('target', e.target.value === '' ? NaN : Number(e.target.value))}
                    />
                  )}
                </Field>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <Field label={t('editor.unit')}>
                  {(p) => (
                    <select
                      {...p}
                      className="select"
                      value={customUnit ? 'custom' : (draft.unit ?? 'count')}
                      onChange={(e) => {
                        const custom = e.target.value === 'custom';
                        setCustomMode(custom);
                        set('unit', e.target.value === 'count' ? null : custom ? '' : e.target.value);
                      }}
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {unitLabel(m, u)}
                        </option>
                      ))}
                      <option value="custom">{t('editor.unitCustom')}</option>
                    </select>
                  )}
                </Field>
              </div>
            </div>
          )}
          {showTarget && customUnit && (
            <div className="row row--wrap" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 140px' }}>
                <Field label={t('editor.unitOne')} help={t('editor.unitCustomHelp')} error={unitError}>
                  {(p) => (
                    <input {...p} className="input" maxLength={11} placeholder={t('editor.unitOnePlaceholder')} value={unitOne} onChange={(e) => setCustomUnit(e.target.value, unitOther)} />
                  )}
                </Field>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <Field label={t('editor.unitOther')}>
                  {(p) => (
                    <input {...p} className="input" maxLength={12} placeholder={unitOne || t('editor.unitOtherPlaceholder')} value={unitOther} onChange={(e) => setCustomUnit(unitOne, e.target.value)} />
                  )}
                </Field>
              </div>
            </div>
          )}
          {averageAllowed(draft) && (
            <>
              <Switch
                label={t('editor.average')}
                help={t('editor.averageHelp')}
                checked={draft.average !== null}
                onChange={(on) => set('average', on ? 7 : null)}
              />
              {draft.average !== null && (
                <Field label={t('editor.averageWindow')}>
                  {(p) => (
                    <input
                      {...p}
                      className="input num"
                      type="number"
                      inputMode="numeric"
                      min={2}
                      max={30}
                      step={1}
                      value={draft.average ?? 7}
                      onChange={(e) => set('average', Math.min(30, Math.max(2, Math.round(Number(e.target.value) || 2))))}
                    />
                  )}
                </Field>
              )}
            </>
          )}
          {(draft.kind === 'quit' || draft.goalType === 'numeric') && (
            <Field label={t('editor.increment')} help={t('editor.incrementHelp')}>
              {(p) => (
                <input
                  {...p}
                  className="input num"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  step="any"
                  value={draft.defaultIncrement}
                  onChange={(e) => set('defaultIncrement', Number(e.target.value))}
                />
              )}
            </Field>
          )}
        </section>
      )}

      {draft.kind !== 'task' && (
        <section className="form-section" aria-label={t('editor.frequency')}>
          <h2>{t('editor.frequency')}</h2>
          <Field label={t('editor.frequency')}>
            {(p) => (
              <select {...p} className="select" value={s.type} onChange={(e) => setFreq(e.target.value as FreqType)}>
                {freqOptions.map((f) => (
                  <option key={f} value={f}>
                    {t(`editor.freq.${f}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {s.type === 'weekdays' && (
            <div role="group" aria-label={t('editor.freq.weekdays')} className="daypicker">
              {m.weekdays.short.map((label, i) => {
                const d = i + 1;
                const on = s.days.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    className="chip-toggle"
                    aria-pressed={on}
                    aria-label={m.weekdays.long[i]}
                    onClick={() => set('schedule', { ...s, days: on ? s.days.filter((x) => x !== d) : [...s.days, d].sort() })}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {s.type === 'weekdays' && (
            <Field label={t('editor.everyWeeks')}>
              {(p) => (
                <select
                  {...p}
                  className="select"
                  value={s.everyWeeks ?? 1}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    const { everyWeeks: _drop, ...rest } = s;
                    set('schedule', n > 1 ? { ...rest, everyWeeks: n } : rest);
                  }}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {t('editor.everyWeeksOption', { count: n })}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
          {s.type === 'yearly' && <p className="muted small">{t('editor.yearlyHelp', { date: fmtDate(draft.startDate, { day: 'numeric', month: 'long' }) })}</p>}
          {s.type === 'monthdays' && (
            <>
              <div role="group" aria-label={t('editor.monthDays')} className="daypicker daypicker--month">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                  const on = s.days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      className="chip-toggle num"
                      aria-pressed={on}
                      onClick={() => set('schedule', { ...s, days: on ? s.days.filter((x) => x !== d) : [...s.days, d].sort((a, b) => a - b) })}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <Switch label={t('editor.lastDay')} checked={Boolean(s.lastDay)} onChange={(v) => set('schedule', { ...s, lastDay: v })} />
            </>
          )}
          {isQuota && draft.kind === 'build' && (
            <Segmented
              legend={t('editor.quotaMode')}
              value={s.mode}
              onChange={(mode) =>
                set('schedule', mode === 'times' ? { ...s, mode, times: s.times ?? 3 } : { type: s.type, mode })
              }
              options={[
                { value: 'times', label: t('editor.quotaTimes') },
                { value: 'amount', label: t('editor.quotaAmount') },
              ]}
            />
          )}
          {isQuota && s.mode === 'times' && (
            <Field label={s.type === 'weekly_quota' ? t('editor.timesPerWeek') : t('editor.timesPerMonth')}>
              {(p) => (
                <input
                  {...p}
                  className="input num"
                  type="number"
                  min={1}
                  max={s.type === 'weekly_quota' ? 7 : 31}
                  value={s.times ?? 1}
                  onChange={(e) => set('schedule', { ...s, times: Math.round(Number(e.target.value)) })}
                />
              )}
            </Field>
          )}
          {s.type === 'interval_days' && (
            <Field label={t('editor.everyNDays')}>
              {(p) => (
                <input {...p} className="input num" type="number" min={1} max={365} value={s.every} onChange={(e) => set('schedule', { ...s, every: Math.round(Number(e.target.value)) })} />
              )}
            </Field>
          )}
          {submitted && errors.filter((e) => e !== 'invalid_target' && e !== 'end_before_start').map((e) => (
            <p key={e} className="field__error" role="alert">
              {errText(e)}
            </p>
          ))}
        </section>
      )}

      <section className="form-section">
        <div className="row row--wrap" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 160px' }}>
            <Field label={t('editor.startDate')}>
              {(p) => <input {...p} className="input" type="date" value={draft.startDate} max={draft.kind === 'task' ? undefined : '2999-12-31'} onChange={(e) => e.target.value && set('startDate', e.target.value)} />}
            </Field>
          </div>
          {draft.kind === 'task' ? (
            <div style={{ flex: '1 1 160px' }}>
              <Field label={`${t('editor.dueDate')} (${t('common.optional')})`}>
                {(p) => <input {...p} className="input" type="date" value={draft.dueDate ?? ''} onChange={(e) => set('dueDate', e.target.value || null)} />}
              </Field>
              {draft.dueDate && (
                <Field label={`${t('editor.dueTime')} (${t('common.optional')})`}>
                  {(p) => <input {...p} className="input num" type="time" value={draft.dueTime ?? ''} onChange={(e) => set('dueTime', e.target.value || null)} />}
                </Field>
              )}
            </div>
          ) : (
            <div style={{ flex: '1 1 160px' }}>
              <Field label={`${t('editor.endDate')} (${t('common.optional')})`} error={submitted && errors.includes('end_before_start') ? errText('end_before_start') : null}>
                {(p) => <input {...p} className="input" type="date" min={draft.startDate} value={draft.endDate ?? ''} onChange={(e) => set('endDate', e.target.value || null)} />}
              </Field>
            </div>
          )}
        </div>
        {draft.kind !== 'quit' && (
          <Field label={`${t('editor.duration')} (${t('common.optional')})`} help={t('editor.durationHelp')}>
            {(p) => <DurationInput {...p} value={draft.durationMinutes} onChange={(v) => set('durationMinutes', v)} />}
          </Field>
        )}
      </section>

      <section className="form-section" data-color={draft.color} aria-label={t('editor.appearance')}>
        <h2>{t('editor.appearance')}</h2>
        <div className="field">
          <span className="field__label" id="icon-label">
            {t('editor.icon')}
          </span>
          <div className="icon-grid" role="group" aria-labelledby="icon-label">
            {HABIT_ICONS.map((ic) => (
              <button key={ic} type="button" className="icon-choice" aria-pressed={draft.icon === ic} aria-label={ic} onClick={() => set('icon', ic)}>
                <HabitIcon name={ic} />
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span className="field__label" id="color-label">
            {t('editor.color')}
          </span>
          <div className="swatches" role="group" aria-labelledby="color-label">
            {HABIT_COLORS.map((c) => (
              <button key={c} type="button" className="swatch" data-color={c} aria-pressed={draft.color === c} aria-label={c} onClick={() => set('color', c)} />
            ))}
          </div>
        </div>
        {groups.length > 0 && (
          <>
            <Field label={t('editor.group')}>
              {(p) => (
                <select {...p} className="select" value={draft.primaryGroupId ?? ''} onChange={(e) => set('primaryGroupId', e.target.value || null)}>
                  <option value="">{t('common.none')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {groups.filter((g) => g.id !== draft.primaryGroupId).length > 0 && (
              <div className="field">
                <span className="field__label" id="sec-groups">
                  {t('editor.secondaryGroups')}
                </span>
                <div className="row row--wrap" role="group" aria-labelledby="sec-groups">
                  {groups
                    .filter((g) => g.id !== draft.primaryGroupId)
                    .map((g) => {
                      const on = draft.secondaryGroupIds.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          className="chip-toggle"
                          style={{ padding: '0 14px' }}
                          aria-pressed={on}
                          onClick={() => set('secondaryGroupIds', on ? draft.secondaryGroupIds.filter((x) => x !== g.id) : [...draft.secondaryGroupIds, g.id])}
                        >
                          {g.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </>
        )}
        {draft.kind !== 'task' && <Switch label={t('editor.hideStreak')} checked={draft.hideStreak} onChange={(v) => set('hideStreak', v)} />}
        {draft.kind !== 'task' && (
          <Switch label={t('editor.excludedFromProgress')} help={t('editor.excludedFromProgressHelp')} checked={draft.excludedFromProgress} onChange={(v) => set('excludedFromProgress', v)} />
        )}
      </section>

      {isEdit && configChanged && (
        <section className="form-section">
          <Switch label={t('editor.applyHistory')} help={t('editor.applyHistoryHelp')} checked={applyHistory} onChange={setApplyHistory} />
          {impact && (
            <p className="banner" role="status" style={{ margin: 0 }}>
              {t('editor.applyHistoryImpact', { count: impact.changed })}
            </p>
          )}
        </section>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn--outline" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" style={{ flex: 1 }} disabled={saving}>
          {isEdit ? t('common.save') : t('common.create')}
        </button>
      </div>
    </form>
  );
}

/** Minutos con texto local: se puede escribir "4" camino de "45" sin que el campo se vacíe. */
function DurationInput({ value, onChange, ...p }: { value: number | null; onChange: (v: number | null) => void; id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) {
  const [text, setText] = useState(value === null ? '' : String(value));
  return (
    <input
      {...p}
      className="input num"
      type="number"
      inputMode="numeric"
      min={5}
      max={720}
      step={5}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = Math.round(Number(e.target.value));
        onChange(e.target.value && n >= 5 && n <= 720 ? n : null);
      }}
    />
  );
}
